import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import Loader from "../common/Loader.jsx";
import {
  Spinner,
  Row,
  Col,
  ToastContainer,
  Toast,
  Button,
  Dropdown,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";

// Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canEdit, canDelete } from "../common/permissionUtils";
import {
  getQuotationDataApi,
  getQuotationHistoriesApi,
  updateQuotationStatusApi,
  getQuotationAttachmentsApi,
  removeQuotationAttachmentApi,
  getQuotationWorkflowApi,
  getEmailTemplatesApi,
  previewQuotationTemplateApi,
} from "./quotation-api";
import {
  sanitizeInput,
  sanitizeEmail,
  sanitizePhoneNumber,
  sanitizeNumericInput,
} from "../common/security-utils";
import "./quotation.css";
import Back from "../../assets/svg/back.svg";
import ProfileIc from "../../assets/png/profile-ic.png";
import { ReactComponent as DeleteRow } from "../../assets/svg/delete.svg";
import { ReactComponent as MailIC } from "../../assets/svg/mail_icv.svg";
import { ReactComponent as CallIc } from "../../assets/svg/call_icv.svg";
import { ReactComponent as BuildIc } from "../../assets/svg/build_icv.svg";
import { ReactComponent as ITICs } from "../../assets/svg/it_icv.svg";
import { ReactComponent as LocatIc } from "../../assets/svg/location_icv.svg";
import { ReactComponent as HistoryIc } from "../../assets/svg/history_icv.svg";

import { ReactComponent as EyeIc } from "../../assets/svg/eye_ic.svg";
import { ReactComponent as QuotaIC } from "../../assets/svg/quotai_ic.svg";
import { ReactComponent as QuotaIC1 } from "../../assets/svg/quoti_isd.svg";
import { ReactComponent as LocsIC } from "../../assets/svg/loc_icv.svg";
import { ReactComponent as CenterIc } from "../../assets/svg/start-ic.svg";
import { ReactComponent as EndIC } from "../../assets/svg/end-ic.svg";
import { ReactComponent as StartIC } from "../../assets/svg/center-ic.svg";

import { ReactComponent as SelCenterIc } from "../../assets/svg/select_center.svg";
import { ReactComponent as SelectEnd } from "../../assets/svg/select_end.svg";
import { ReactComponent as SelectReject } from "../../assets/svg/reject_iced.svg";
import { ReactComponent as RightArrow } from "../../assets/svg/right_arrow.svg";
import { ReactComponent as PersonIC } from "../../assets/svg/person-ic.svg";
import DeleteModal from "../common/delete-modal";
import ComposeModal from "./email-module";
import AttachmentDeleteModal from "../common/AttachmentDeleteModal";
import LeadConversionModal from "../leads/lead-conversion-modal.jsx";
import { checkLeadConversionApi, getLeadDataApi, updateLeadStatusApi } from "../leads/leads-api.js";
import ExtendValidityModal from "./ExtendValidityModal";
import { applyStateTaxFlags } from "../common/state-tax";
import AiSummaryCard from "../common/ai-summary-card.js";
import { getQuotationAiSummaryApi } from "./quotation-api";
import {
  FaDownload,
  FaEye,
  FaTrash,
  FaInfoCircle,
  FaFileAlt,
  FaPaperPlane,
  FaHandshake,
  FaThumbsUp,
  FaThumbsDown,
  FaShoppingCart,
  FaClock,
  FaWhatsapp,
} from "react-icons/fa";

// Step constants defined outside component to prevent recreation
const FULL_Cust_R_STEPS = ["Draft", "Approved", "Sent", "Customer Rejected"];
const FULL_Cust_A_STEPS = ["Draft", "Approved", "Sent", "Customer Accepted", "Sale Order"];
const FULL_STEPS = ["Draft", "Approved", "Sent", "Negotiate"];
// Negotiate isn't a dead end for the stepper — same as Sent, the next
// clickable step from Negotiate should still be Customer Accepted (Customer
// Rejected stays reachable via the separate button pair below).
const NEGOTIATE_STEPS = ["Draft", "Approved", "Sent", "Negotiate", "Customer Accepted"];
const REJECTED_STEPS = ["Draft", "Rejected"];
const DRAFT_STEPS = ["Draft"];

// ⓘ icon with a breakdown tooltip rendered in a body portal so it is never
// clipped by the table/card overflow. `rows` is an array of [label, value,
// isTotal?] tuples shown above the icon.
const InfoTooltip = ({ rows }) => {
  const iconRef = useRef(null);
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const openTooltip = () => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (rect) {
      setPos({ top: rect.top - 8, left: rect.right });
      setShow(true);
    }
  };
  const closeTooltip = () => setShow(false);

  return (
    <span
      ref={iconRef}
      className="total-info-wrap"
      onMouseEnter={openTooltip}
      onMouseLeave={closeTooltip}
    >
      <FaInfoCircle className="total-info-icon" size={13} />
      {show &&
        createPortal(
          <span
            className="total-info-tooltip"
            style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
          >
            {rows.map(([label, value, isTotal], i) => (
              <span key={i} className={`tt-row${isTotal ? " tt-total" : ""}`}>
                <span>{label}</span>
                <span>{value}</span>
              </span>
            ))}
          </span>,
          document.body,
        )}
    </span>
  );
};

const QuotationView = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  // 🔒 Permissions
  const { rolePermissionList } = useGlobal();
  const hasEditPermission = canEdit(rolePermissionList, "Quotation");
  const hasDeletePermission = canDelete(rolePermissionList, "Quotation");

  // 🔒 VAPT Security: Validate ID parameter
  const isValidId = useCallback((id) => {
    if (!id) return false;
    // Allow alphanumeric, hyphens, and underscores only
    return /^[a-zA-Z0-9_-]+$/.test(String(id));
  }, []);

  // 🔒 Security Constants
  const MAX_NUMERIC_VALUE = 999999999;

  const [productList, setProductList] = useState([]);
  const [quotation, setQuotation] = useState(null);
  // PDF pre-fetched ahead of the Share click so navigator.share() can run
  // synchronously within the click's user-gesture (see prefetchSharePdf).
  const [sharePdfFile, setSharePdfFile] = useState(null);
  const [isQuotView, setIsQuotView] = useState(false);
  // PDF URL for the template-preview overlay (reuses the isQuotView iframe
  // UI, but points at a template-rendered PDF instead of quotation.pdf_view).
  const [templatePreviewUrl, setTemplatePreviewUrl] = useState(null);
  const [quotHistory, setQuotHistory] = useState([]);
  const [isIgst, setIsIgst] = useState(false);
  const [isCgst, setIsCgst] = useState(false);
  const [isSgst, setIsSgst] = useState(false);
  const [show, setShow] = useState(false);
  const [emailId, setEmailID] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [activeValue, setActiveValue] = useState("Draft");

  // Email templates with code = "Quotation", for the template-select dropdown.
  const [quotationTemplates, setQuotationTemplates] = useState([]);
  const [previewingTemplateId, setPreviewingTemplateId] = useState(null);
  // Template last selected via the "Template" dropdown — when set, the mail
  // popup uses this template's content instead of the default mail body.
  const [selectedMailTemplateId, setSelectedMailTemplateId] = useState(null);
  const [isLeadConvertOpen, setIsLeadConvertOpen] = useState(false);
  const [leadConversionInfo, setLeadConversionInfo] = useState(null);
  const [convertLead, setConvertLead] = useState(null);

  const [steps, setSteps] = useState(FULL_STEPS);
  const backendStepIndex = steps.indexOf(activeValue);
  const safeBackendStep = backendStepIndex === -1 ? 0 : backendStepIndex;

  const toNum = (v) => {
    const n = Number.parseFloat(v);
    return Number.isNaN(n) ? 0 : n;
  };

  // Decode an HTML entity symbol (e.g. "&#8377;" -> "₹") to a plain character.
  const decodeHtmlEntity = (str) => {
    if (!str) return "";
    const el = document.createElement("textarea");
    el.innerHTML = str;
    return el.value;
  };

  // Symbol of the quotation's saved currency (from the backend); falls back
  // to ₹ when none is set.
  const currencySymbol = quotation?.currency_symbol
    ? decodeHtmlEntity(quotation.currency_symbol)
    : "₹";

  // Organization's own currency — used to detect a mismatch against the
  // quotation/customer currency below.
  const orgCurrencySymbol = decodeHtmlEntity(quotation?.org_currency_symbol) || "";
  const orgCurrencyName = quotation?.org_currency_name || "";

  // Total discount for the quotation, split equally across line items — same
  // rule as quotation-create. Tax is charged on the post-discount amount.
  const totalDiscount = toNum(quotation?.total_discount);
  const itemDiscount =
    totalDiscount > 0 && productList.length > 0
      ? totalDiscount / productList.length
      : 0;
  const hasDiscount = totalDiscount > 0;

  // Break a line item into its parts: base, per-item discount, taxable amount,
  // per-tax amounts, and the discounted+taxed total.
  const getItemBreakdown = (row) => {
    const baseAmount = toNum(row.quantity) * toNum(row.unit_price);
    const taxableAmount = Math.max(baseAmount - itemDiscount, 0);
    const cgst = toNum(row.cgst_percentage);
    const sgst = toNum(row.sgst_percentage);
    const igst = toNum(row.igst_percentage);
    const cgstAmount = taxableAmount * (cgst / 100);
    const sgstAmount = taxableAmount * (sgst / 100);
    const igstAmount = taxableAmount * (igst / 100);
    const taxAmount = cgstAmount + sgstAmount + igstAmount;
    return {
      baseAmount,
      itemDiscount,
      taxableAmount,
      cgst,
      sgst,
      igst,
      cgstAmount,
      sgstAmount,
      igstAmount,
      taxAmount,
      total: taxableAmount + taxAmount,
    };
  };

  // Aggregate GST across all items (on the post-discount base).
  const gstTotals = productList.reduce(
    (acc, row) => {
      const bd = getItemBreakdown(row);
      acc.cgst += bd.cgstAmount;
      acc.sgst += bd.sgstAmount;
      acc.igst += bd.igstAmount;
      return acc;
    },
    { cgst: 0, sgst: 0, igst: 0 },
  );
  const totalGst = gstTotals.cgst + gstTotals.sgst + gstTotals.igst;
  const money = (n) =>
    `${currencySymbol}${toNum(n).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  const updateStateTaxFlags = async (stateId) => {
    await applyStateTaxFlags(stateId, {
      setIsIgst,
      setIsCgst,
      setIsSgst,
    });
  };

  const isExpired = Boolean(
    quotation?.is_expired ||
      (quotation?.status !== "Sale Order" &&
        quotation?.status !== "Rejected" &&
        quotation?.status !== "Customer Accepted" &&
        quotation?.valid_date &&
        new Date(quotation.valid_date + "T23:59:59") < new Date()),
  );

  const [active, setActive] = useState("details");
  //Delete
  const [deleteId, setDeleteId] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [previewModal, setPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [attachmentDeleteModal, setAttachmentDeleteModal] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Delete (history)
  const [historyDeleteId, setHistoryDeleteId] = useState(null);
  const [isOpenHistoryDelete, setIsOpenHistoryDelete] = useState(false);

  // Workflow tab
  const [workflowList, setWorkflowList] = useState([]);
  const [isWorkflowLoading, setIsWorkflowLoading] = useState(false);

  // Each workflow status gets a distinct, meaningful icon + color so the
  // timeline communicates what actually happened at a glance, rather than
  // every "positive" status collapsing into the same generic checkmark.
  const workflowIcons = {
    created: <FaFileAlt style={{ color: "#172B4D" }} />,
    draft: <FaFileAlt style={{ color: "#8790A1" }} />,
    approved: <FaThumbsUp style={{ color: "#2E7D32" }} />,
    sent: <FaPaperPlane style={{ color: "#3D4AF5" }} />,
    negotiate: <FaHandshake style={{ color: "#B8860B" }} />,
    "sale order": <FaShoppingCart style={{ color: "#2E7D32" }} />,
    "converted to sale order": <FaShoppingCart style={{ color: "#2E7D32" }} />,
    "customer accepted": <FaThumbsUp style={{ color: "#2E7D32" }} />,
    rejected: <FaThumbsDown style={{ color: "#D32F2F" }} />,
    "customer rejected": <FaThumbsDown style={{ color: "#D32F2F" }} />,
    expired: <FaClock style={{ color: "#D32F2F" }} />,
    "validity extended": <FaClock style={{ color: "#2E7D32" }} />,
    pending: <FaClock style={{ color: "#B8860B" }} />,
  };

  // Extend validity modal state
  const [showExtendModal, setShowExtendModal] = useState(false);

  const handleExtendSuccess = (response) => {
    setShowExtendModal(false);
    setSnackbarMessage(response?.message || "Quotation validity extended successfully");
    setSnackbarStatus(true);
    handleSnackbarShow();
    fetchQuotation();
    fetchQuotationHistories();
    if (active === "workflow") {
      fetchWorkflow();
    }
  };
  useEffect(() => {
    if (deleteId != null) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);
  useEffect(() => {
    if (emailId != null) {
      setShow(true);
    }
  }, [emailId]);

  useEffect(() => {
    const fetchQuotationTemplates = async () => {
      try {
        const response = await getEmailTemplatesApi("Quotation");
        const templates = response?.data || [];
        setQuotationTemplates(templates);
        // If there's only one template, default to it instead of making the
        // user open the dropdown to pick the sole option.
        if (templates.length === 1) {
          setSelectedMailTemplateId(templates[0].id);
        }
      } catch (err) {
        console.error("Failed to load quotation templates:", err);
      }
    };
    fetchQuotationTemplates();
  }, []);

  const cancelDelete = () => {
    setIsOpenDelete(false);
  };
  const onSuccessDelete = (response) => {
    setDeleteId(null);
    setSnackbarMessage(response.message);
    setSnackbarStatus(response.success);
    handleSnackbarShow();
    if (response.success === true) {
      setIsOpenDelete(false);
      navigate(-1);
    }
  };

  const cancelHistoryDelete = () => {
    setIsOpenHistoryDelete(false);
    setHistoryDeleteId(null);
  };
  const onSuccessHistoryDelete = (response) => {
    setSnackbarMessage(response.message);
    setSnackbarStatus(response.success);
    handleSnackbarShow();
    setIsOpenHistoryDelete(false);
    setHistoryDeleteId(null);
    if (response.success === true) {
      fetchWorkflow();
    }
  };
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  const fetchQuotationHistories = useCallback(async () => {
    // 🔒 VAPT Security: Validate ID before API call
    if (!isValidId(id)) {
      navigate("/quotation/list");
      return;
    }

    try {
      const response = await getQuotationHistoriesApi(id);
      // 🔒 VAPT: Sanitize history data
      const sanitizedHistory = (response?.data ?? response)?.map((item) => ({
        ...item,
        status: sanitizeInput(item.status ?? ""),
        created_by: sanitizeInput(item.created_by ?? ""),
        note: sanitizeInput(item.note ?? ""),
      }));
      setQuotHistory(sanitizedHistory ?? []);
    } catch (err) {
      // Error handled silently
    }
  }, [id, isValidId, navigate]);

  const fetchWorkflow = useCallback(async () => {
    if (!isValidId(id)) return;
    setIsWorkflowLoading(true);
    try {
      const response = await getQuotationWorkflowApi(id);
      const data = response?.data ?? response;
      setWorkflowList(Array.isArray(data?.workflow) ? data.workflow : []);
    } catch (err) {
      // Error handled silently
    } finally {
      setIsWorkflowLoading(false);
    }
  }, [id, isValidId]);

  const fetchQuotation = useCallback(async () => {
    // 🔒 VAPT Security: Validate ID before API call
    if (!isValidId(id)) {
      navigate("/quotation/list");
      return;
    }

    try {
      const response = await getQuotationDataApi(id);
      const data = response?.data ?? {};

      // 🔒 VAPT: Sanitize all text fields from API response
      const sanitizedData = {
        ...data,
        quotation_id: sanitizeInput(data.quotation_id ?? ""),
        sale_order_code: sanitizeInput(data.sale_order_code ?? ""),
        title: sanitizeInput(data.title ?? ""),
        subject: sanitizeInput(data.subject ?? ""),
        description: sanitizeInput(data.description ?? ""),
        customer: sanitizeInput(data.customer ?? ""),
        lead: sanitizeInput(data.lead ?? ""),
        email: sanitizeEmail(data.email ?? ""),
        phone: sanitizePhoneNumber(data.phone ?? ""),
        gst_number: sanitizeInput(data.gst_number ?? ""),
        bill_address: sanitizeInput(data.bill_address ?? ""),
        bill_city: sanitizeInput(data.bill_city ?? ""),
        bill_state: sanitizeInput(data.bill_state ?? ""),
        bill_zipcode: sanitizeInput(data.bill_zipcode ?? ""),
        bill_country: sanitizeInput(data.bill_country ?? ""),
        ship_address: sanitizeInput(data.ship_address ?? ""),
        ship_city: sanitizeInput(data.ship_city ?? ""),
        ship_state: sanitizeInput(data.ship_state ?? ""),
        ship_zipcode: sanitizeInput(data.ship_zipcode ?? ""),
        ship_country: sanitizeInput(data.ship_country ?? ""),
        customer_note: sanitizeInput(data.customer_note ?? ""),
        terms_conditions: sanitizeInput(data.terms_conditions ?? ""),
        status: sanitizeInput(data.status ?? ""),
        // 🔒 VAPT: Sanitize numeric values
        sub_total: sanitizeNumericInput(data.sub_total ?? 0, MAX_NUMERIC_VALUE),
        total_amount: sanitizeNumericInput(data.total_amount ?? 0, MAX_NUMERIC_VALUE),
        total_discount: sanitizeNumericInput(data.total_discount ?? 0, MAX_NUMERIC_VALUE),
        tds_amount: sanitizeNumericInput(data.tds_amount ?? 0, MAX_NUMERIC_VALUE),
        tcs_amount: sanitizeNumericInput(data.tcs_amount ?? 0, MAX_NUMERIC_VALUE),
        adjustments: sanitizeNumericInput(data.adjustments ?? 0, MAX_NUMERIC_VALUE),
        tds_percentage: sanitizeNumericInput(data.tds_percentage ?? 0, 100),
        tcs_percentage: sanitizeNumericInput(data.tcs_percentage ?? 0, 100),
      };

      setQuotation(sanitizedData);
      setActiveValue(sanitizeInput(data.status ?? ""));

      // highlight
      let selectedSteps = [];

      if (response?.data.status === "Rejected") {
        selectedSteps = REJECTED_STEPS;
      } else if (
        response?.data.status === "Customer Accepted" ||
        response?.data.status === "Sale Order"
      ) {
        selectedSteps = FULL_Cust_A_STEPS;
      } else if (response?.data.status === "Customer Rejected") {
        selectedSteps = FULL_Cust_R_STEPS;
      } else if (response?.data.status === "Negotiate") {
        selectedSteps = NEGOTIATE_STEPS;
      } else {
        selectedSteps = FULL_STEPS;
      }

      setSteps(selectedSteps);

      // NOW safe to calculate active step
      setActiveStep(selectedSteps.indexOf(sanitizedData.status));

      // 🔒 VAPT: Validate state_id before fetching state tax
      if (isValidId(data?.state_id)) {
        updateStateTaxFlags(data.state_id);
      }

      // 🔒 VAPT: Sanitize line items
      const sanitizedLineItems = (data?.line_items || []).map((item) => ({
        ...item,
        id: isValidId(item.id) ? item.id : "",
        product_name: sanitizeInput(item.product_name || item.service_name || ""),
        hsn_no: sanitizeInput(item.hsn_no ?? item.product?.hsn_no ?? item.product?.hsn ?? ""),
        uom: typeof item.uom === 'object' ? sanitizeInput(item.uom?.label ?? "") : sanitizeInput(item.uom ?? ""),
        quantity: sanitizeNumericInput(item.quantity ?? 0, MAX_NUMERIC_VALUE),
        unit_price: sanitizeNumericInput(
          item.unit_price ?? 0,
          MAX_NUMERIC_VALUE,
        ),
        cgst_percentage: sanitizeNumericInput(item.cgst_percentage ?? 0, 100),
        sgst_percentage: sanitizeNumericInput(item.sgst_percentage ?? 0, 100),
        igst_percentage: sanitizeNumericInput(item.igst_percentage ?? 0, 100),
        total_amount: sanitizeNumericInput(
          item.total_amount ?? 0,
          MAX_NUMERIC_VALUE,
        ),
      }));
      setProductList(sanitizedLineItems);
    } catch (err) {
      // Error handled silently
    }
  }, [id, isValidId, navigate, MAX_NUMERIC_VALUE]);

  const fetchAttachments = useCallback(async () => {
    // 🔒 VAPT Security: Validate ID before API call
    if (!isValidId(id)) {
      return;
    }

    try {
      const response = await getQuotationAttachmentsApi(id);
      const attachmentList = response?.data?.attachments ?? [];

      // 🔒 VAPT: Sanitize attachment data
      const sanitizedAttachments = attachmentList.map((att) => ({
        ...att,
        file_name: sanitizeInput(att.file_name ?? ""),
        first_name: sanitizeInput(att.first_name ?? ""),
        date: sanitizeInput(att.date ?? ""),
        file_size: sanitizeInput(String(att.file_size ?? "0")),
        file_path: sanitizeInput(att.file_path ?? ""),
      }));

      setAttachments(sanitizedAttachments);
    } catch (err) {
      // Error handled silently
      setAttachments([]);
    }
  }, [id, isValidId]);

  useEffect(() => {
    fetchQuotation();
    fetchQuotationHistories();
    fetchAttachments();
  }, [id, fetchQuotation, fetchQuotationHistories, fetchAttachments]);

  useEffect(() => {
    if (active === "workflow") {
      fetchWorkflow();
    }
  }, [active, fetchWorkflow]);

  const openLeadConversion = async () => {
    try {
      const checkRes = await checkLeadConversionApi(quotation?.lead_id);
      setLeadConversionInfo(checkRes?.data || checkRes);
    } catch (e) {
      setLeadConversionInfo(null);
    }
    // The quotation only carries the lead's id — load its name and contact
    // details so the conversion modal isn't blank.
    try {
      const leadRes = await getLeadDataApi(quotation?.lead_id);
      setConvertLead(leadRes?.lead ?? null);
    } catch (e) {
      setConvertLead(null);
    }
    setIsLeadConvertOpen(true);
  };

  const handleLeadConverted = async (payload) => {
    const response = await updateLeadStatusApi(payload, quotation?.lead_id);
    if (response?.success) {
      setIsLeadConvertOpen(false);
      updateStatus("Sale Order");
    } else {
      setSnackbarMessage(response?.message || "Failed to convert lead");
      setSnackbarStatus(false);
      handleSnackbarShow();
    }
  };

  const updateStatus = async (value) => {
    // 🔒 VAPT Security: Validate ID and sanitize status value
    if (!isValidId(id)) {
      return;
    }

    setLoadingStatus(value);
    try {
      const bodyData = {
        status: sanitizeInput(value), // Approved, Rejected, Negotiate, Sale Order
      };
      let response;
      if (value === "Negotiate") {
        navigate("/quotation/create", {
          state: { id: id, is_negiotate: true, fromView: true },
        });
      } else {
        response = await updateQuotationStatusApi(bodyData, id);
      }

      if (value === "Sale Order" && response?.data?.error?.code === "LEAD_NOT_CONVERTED") {
        openLeadConversion();
        return;
      }

      // 🔒 VAPT: Sanitize response messages
      setSnackbarMessage(sanitizeInput(response?.data?.message ?? ""));
      setSnackbarStatus(response?.data?.success ?? false);

      handleSnackbarShow();

      // On successful conversion to a Sale Order, jump straight to the
      // newly-created order's edit page instead of staying on the quotation.
      if (
        value === "Sale Order" &&
        response?.data?.success === true &&
        isValidId(response?.data?.order_id)
      ) {
        navigate("/order/create", { state: { id: response.data.order_id } });
        return;
      }

      fetchQuotation();
      fetchQuotationHistories();
      fetchWorkflow();
    } catch (err) {
      if (value === "Sale Order" && err?.response?.data?.error?.code === "LEAD_NOT_CONVERTED") {
        openLeadConversion();
        return;
      }
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to update status";
      setSnackbarMessage(sanitizeInput(errorMsg));
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setLoadingStatus(null);
    }
  };
  const formatAddress = (data = {}) => {
    const { bill_address, bill_city, bill_state, bill_zipcode, bill_country } =
      data;

    return [bill_address, bill_city, bill_state, bill_zipcode, bill_country]
      .filter((item) => item && item !== "null")
      .join(", ");
  };

  const formatShipAddress = (data) => {
    const { ship_address, ship_city, ship_state, ship_zipcode, ship_country } =
      data;

    return [ship_address, ship_city, ship_state, ship_zipcode, ship_country]
      .filter((item) => item && item !== "null") // remove null/empty
      .join(", ");
  };

  const getIcon = (index, step) => {
    // If selected or before selected → show selected icons
    if (index === 0) {
      return activeStep >= 0 ? <StartIC /> : <StartIC />;
    }

    // Last step
    if (index === steps.length - 1) {
      return activeStep >= index ? <SelectEnd /> : <EndIC />;
    }

    // Middle steps
    return activeStep >= index ? <SelCenterIc /> : <CenterIc />;
  };

  const isImageFile = (fileName) => {
    const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp", "svg"];
    const ext = fileName.split(".").pop().toLowerCase();
    return imageExtensions.includes(ext);
  };

  const isPdfFile = (fileName) => {
    const ext = fileName.split(".").pop().toLowerCase();
    return ext === "pdf";
  };

  const isPreviewableFile = (fileName) => {
    return isImageFile(fileName) || isPdfFile(fileName);
  };

  const handlePreviewFile = (attachment) => {
    if (!attachment.file_path) return;

    if (isImageFile(attachment.file_name)) {
      setPreviewData({
        name: attachment.file_name,
        url: attachment.file_path,
        type: "image",
      });
      setPreviewModal(true);
    } else if (isPdfFile(attachment.file_name)) {
      setPreviewData({
        name: attachment.file_name,
        url: attachment.file_path,
        type: "pdf",
      });
      setPreviewModal(true);
    }
  };

  const closePreviewModal = () => {
    setPreviewModal(false);
    setPreviewData(null);
  };

  // Builds the shared message body — used both in the native file-share
  // caption and the wa.me text fallback, so the content stays in sync. The
  // PDF link is always included in the text itself (not just attached as a
  // file) because several share targets — notably Microsoft Teams — silently
  // drop the `files` payload from navigator.share() and only pass text/url
  // through, so the recipient still needs a way to reach the PDF.
  const buildQuotationShareText = (pdfUrl) => {
    const customerName = quotation?.customer || "Customer";
    const title = quotation?.title || "your";
    const lines = [
      `Hello ${customerName},`,
      `Sharing the quotation details for the ${title} project.`,
      "",
      `Quotation No: ${quotation?.quotation_id ?? ""}`,
      quotation?.total_amount != null ? `Amount: ${money(toNum(quotation.total_amount))}` : "",
      quotation?.valid_date ? `Valid Until: ${quotation.valid_date}` : "",
      "",
      "Kindly review and confirm so we can proceed with the next steps.",
    ];
    if (pdfUrl) {
      lines.push("", pdfUrl);
    }
    return lines.filter((line) => line !== undefined && line !== null).join("\n");
  };

  // Falls back to WhatsApp Web/app with a pre-filled message containing the
  // quotation summary and its hosted PDF link — used when the browser can't
  // share actual files (no Web Share API support, e.g. most desktops).
  const shareWhatsappLink = (pdfUrl) => {
    const text = encodeURIComponent(buildQuotationShareText(pdfUrl));
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  };

  // Fetches a quotation PDF ahead of time and returns it as a File, so the
  // click handler can call navigator.share() synchronously. navigator.share()
  // must run inside the original user-gesture call stack — any `await`
  // before it (e.g. fetching the PDF) makes the browser reject it with
  // NotAllowedError, since by the time the promise resolves the browser no
  // longer considers the click "in progress".
  const fetchSharePdfFile = async (pdfUrl, fileName) => {
    const response = await fetch(pdfUrl, { method: "GET", headers: { Accept: "*/*" } });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    const blob = await response.blob();
    return new File([blob], fileName, { type: blob.type || "application/pdf" });
  };

  const prefetchSharePdf = useCallback(async () => {
    if (!quotation?.pdf_view) return;
    try {
      const fileName = `Quotation-${quotation?.quotation_id ?? "quote"}.pdf`;
      setSharePdfFile(await fetchSharePdfFile(quotation.pdf_view, fileName));
    } catch (error) {
      // Sharing still works via the wa.me link fallback even if this fails.
      console.error("Failed to pre-fetch quotation PDF for sharing:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotation?.pdf_view, quotation?.quotation_id]);

  useEffect(() => {
    prefetchSharePdf();
  }, [prefetchSharePdf]);

  // Pre-fetched File for whichever template preview PDF is currently open,
  // mirroring sharePdfFile's role for the quotation's own PDF.
  const [templateSharePdfFile, setTemplateSharePdfFile] = useState(null);
  useEffect(() => {
    if (!templatePreviewUrl) {
      setTemplateSharePdfFile(null);
      return;
    }
    let cancelled = false;
    fetchSharePdfFile(templatePreviewUrl, `Quotation-Template-${quotation?.quotation_id ?? "quote"}.pdf`)
      .then((file) => {
        if (!cancelled) setTemplateSharePdfFile(file);
      })
      .catch((error) => {
        console.error("Failed to pre-fetch template PDF for sharing:", error);
      });
    return () => {
      cancelled = true;
    };
  }, [templatePreviewUrl, quotation?.quotation_id]);

  // Shares whichever PDF is currently relevant (the quotation's own PDF, or
  // an open template preview). Prefers the native Web Share API with the
  // actual PDF file attached (so it lands in WhatsApp as a real document) —
  // only supported on browsers that implement navigator.canShare({ files }),
  // and only when the PDF was already pre-fetched. The shared text always
  // carries the PDF link too, since some share targets (e.g. Teams) ignore
  // the attached file. Falls back to the wa.me text+link share otherwise.
  const shareQuotationPdf = (pdfUrl, pdfFile) => {
    if (!pdfUrl) {
      setSnackbarMessage("⚠ PDF not available to share yet");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }

    const canTryFileShare =
      pdfFile &&
      typeof navigator !== "undefined" &&
      typeof navigator.share === "function" &&
      typeof navigator.canShare === "function" &&
      navigator.canShare({ files: [pdfFile] });

    if (canTryFileShare) {
      const shareText = buildQuotationShareText(pdfUrl);
      navigator
        .share({ files: [pdfFile], text: shareText, title: pdfFile.name })
        .catch((error) => {
          if (error?.name === "AbortError") return;
          console.error("Native file share failed, falling back to WhatsApp link:", error);
          shareWhatsappLink(pdfUrl);
        });
      return;
    }

    shareWhatsappLink(pdfUrl);
  };

  const handleShareWhatsapp = () => shareQuotationPdf(quotation?.pdf_view, sharePdfFile);

  const handleShareTemplatePreview = () => shareQuotationPdf(templatePreviewUrl, templateSharePdfFile);

  // Selecting a template only marks it as the one to use for mail/PDF — it
  // no longer generates/opens a preview by itself. Use handleViewTemplatePdf
  // (wired to the "View PDF" button) to actually render it.
  const handleSelectTemplate = (template) => {
    setSelectedMailTemplateId(template.id);
    // A newly selected template invalidates any previously generated preview.
    setTemplatePreviewUrl(null);
  };

  const handleViewTemplatePdf = async () => {
    if (!selectedMailTemplateId) return;
    setPreviewingTemplateId(selectedMailTemplateId);
    try {
      const response = await previewQuotationTemplateApi(id, selectedMailTemplateId);
      const pdfUrl = response?.data?.pdf_url;
      if (!pdfUrl) {
        setSnackbarMessage("⚠ Template preview not available");
        setSnackbarStatus(false);
        handleSnackbarShow();
        return;
      }
      setTemplatePreviewUrl(pdfUrl);
      setIsQuotView(true);
    } catch (error) {
      console.error("Failed to preview quotation template:", error);
      setSnackbarMessage("⚠ Failed to load template preview");
      setSnackbarStatus(false);
      handleSnackbarShow();
    } finally {
      setPreviewingTemplateId(null);
    }
  };

  const handleDownloadAttachment = (attachment) => {
    if (!attachment.file_path && !attachment.file_url) {
      console.error("No file path available for download", attachment);
      setSnackbarMessage("⚠ Download failed: No file path available");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }

    try {
      // Use file_path if available, fallback to file_url
      let fileUrl = attachment.file_path || attachment.file_url;

      if (!fileUrl) {
        throw new Error("No file path or URL available");
      }

      console.log("Downloading from URL:", fileUrl);
      console.log("Attachment data:", attachment);

      // Check if URL is absolute (http/https) or S3 URL
      const isAbsoluteUrl = fileUrl.startsWith('http://') || fileUrl.startsWith('https://') || fileUrl.includes('s3');

      // Only add API base if it's a relative path
      if (!isAbsoluteUrl) {
        fileUrl = fileUrl.replace(/^\/+/, '');
        fileUrl = `${process.env.REACT_APP_API_URL}/${fileUrl}`;
      }

      console.log("Final download URL:", fileUrl);

      // Fetch the file as a blob
      // Note: For S3 URLs, we typically don't need credentials header
      const fetchOptions = {
        method: 'GET',
        headers: {
          'Accept': '*/*'
        }
      };

      // Only add credentials for non-S3 URLs
      if (!fileUrl.includes('s3')) {
        fetchOptions.credentials = 'include';
      }

      fetch(fileUrl, fetchOptions)
        .then(response => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.blob();
        })
        .then(blob => {
          // Create a blob URL instead of using the direct file URL
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = attachment.file_name || "attachment";
          link.style.display = "none";
          document.body.appendChild(link);

          // Trigger download
          link.click();

          // Cleanup
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
          }, 100);

          console.log("Download completed for:", attachment.file_name);
          setSnackbarMessage("✓ Download completed: " + attachment.file_name);
          setSnackbarStatus(true);
          handleSnackbarShow();
        })
        .catch(error => {
          console.error("Download error:", error);
          setSnackbarMessage("⚠ Download failed: " + error.message);
          setSnackbarStatus(false);
          handleSnackbarShow();
        });
    } catch (error) {
      console.error("Download setup error:", error);
      setSnackbarMessage("⚠ Download failed: " + error.message);
      setSnackbarStatus(false);
      handleSnackbarShow();
    }
  };

  const handleDeleteAttachment = (attachmentId, fileName) => {
    // 🔒 VAPT Security: Validate ID before opening modal
    if (!attachmentId || !isValidId(String(attachmentId))) {
      setSnackbarMessage("⚠ Invalid attachment ID");
      setSnackbarStatus(false);
      handleSnackbarShow();
      console.error("Invalid attachment ID");
      return;
    }

    setAttachmentToDelete({ id: attachmentId, name: fileName });
    setAttachmentDeleteModal(true);
  };

  const confirmDeleteAttachment = async () => {
    if (!attachmentToDelete) return;

    try {
      setIsDeleting(true);
      const response = await removeQuotationAttachmentApi(attachmentToDelete.id);

      if (response?.data?.success) {
        // Remove from UI
        setAttachments((prev) =>
          prev.filter((att) => att.id !== attachmentToDelete.id)
        );
        setSnackbarMessage(`✓ Attachment "${attachmentToDelete.name}" deleted successfully`);
        setSnackbarStatus(true);
        handleSnackbarShow();
        console.log(`✓ Attachment ${attachmentToDelete.id} deleted successfully`);
        setAttachmentDeleteModal(false);
        setAttachmentToDelete(null);
      } else {
        setSnackbarMessage(`⚠ Failed to delete attachment. Please try again.`);
        setSnackbarStatus(false);
        handleSnackbarShow();
        console.error("Delete failed:", response?.data);
      }
    } catch (error) {
      setSnackbarMessage(`⚠ Error deleting attachment: ${error.message}`);
      setSnackbarStatus(false);
      handleSnackbarShow();
      console.error("Error deleting attachment:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteAttachment = () => {
    setAttachmentDeleteModal(false);
    setAttachmentToDelete(null);
  };

  return (
    <div
      style={{
        fontFamily: "'Euclid Circular B', sans-serif",

        backgroundColor: "#F7F8FC",
      }}
    >
      <ComposeModal
        id={emailId}
        templateId={selectedMailTemplateId}
        show={show}
        handleClose={() => setShow(false)}
        succesError={(response) => {
          setSnackbarMessage(response.message);
          setSnackbarStatus(response.success);
          handleSnackbarShow();
          if (response.success === true) {
            setShow(false);
            fetchQuotation();
            fetchQuotationHistories();
          }
        }}
      />
      <ExtendValidityModal
        isOpen={showExtendModal}
        quotation={quotation}
        onClose={() => setShowExtendModal(false)}
        onSuccess={handleExtendSuccess}
      />
      <ToastContainer position="top-end">
        <Toast
          className="d-inline-block m-1"
          bg={snackbarStatus === true ? "success" : "danger"}
          show={showSnackbar}
          onClose={handleSnackbarClose}
          delay={3000}
          autohide
        >
          <Toast.Body style={{ color: "#ffffff" }}>
            {snackbarMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>{" "}
      {isOpenDelete && (
        <DeleteModal
          deleteUrl={`quotation/delete/${deleteId}`}
          onSuccess={onSuccessDelete}
          onCancel={cancelDelete}
        />
      )}
      {isOpenHistoryDelete && (
        <DeleteModal
          deleteUrl={`quotation_history/delete/${historyDeleteId}`}
          onSuccess={onSuccessHistoryDelete}
          onCancel={cancelHistoryDelete}
        />
      )}
      <Row
        className="align-items-center justify-content-between"
        style={{
          height: "84px",
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid #E0E0E0",
        }}
      >
        {/* LEFT SECTION */}
        <Col md={12} lg={5} xl={4} >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "36px",
                height: "36px",
                borderRadius: "100px",
                border: "1px solid #D4DDE7",
                marginLeft: "16px",
                cursor: "pointer",
              }}
            >
              <img
                src={Back}
                style={{ width: "36px" }}
                alt="Back"
                onClick={() => navigate("/quotation/list")}
              />
            </div>

            <div
              style={{
                width: "44px",
                height: "44px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              <img
                src={ProfileIc}
                alt="Profile"
                className="profile-img"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: "50%",
                }}
              />
            </div>

            <div className="d-flex flex-column">
              <div className="fs-20 fw-500 text-dkblue d-flex align-items-center">
                {quotation?.customer ?? "--"}{" "}
                <span className={`ms-2 status-type`}>{quotation?.type}</span>
                {isExpired && (
                  <span
                    className="ms-2"
                    style={{
                      backgroundColor: "#FFEDE0",
                      color: "#E65100",
                      padding: "4px 10px",
                      borderRadius: "200px",
                      fontSize: "12px",
                      fontWeight: 600,
                    }}
                  >
                    Expired
                  </span>
                )}
              </div>

              <div className="mt-0">
                <span className="fs-14 fw-500 text-grey">Quotation : </span>
                <span className="fs-14 fw-500 textblue">
                  {quotation?.quotation_id ?? "--"}
                </span>
                {quotation?.sale_order_code && (
                  <>
                    <span className="fs-14 fw-500 text-grey ms-2">Sale Order : </span>
                    <span
                      className="fs-14 fw-500 textblue"
                      style={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={() =>
                        navigate("/order/view", {
                          state: { id: quotation.sale_order_id },
                        })
                      }
                    >
                      {quotation.sale_order_code}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </Col>

        {/* RIGHT SECTION */}
        <Col md={12} lg={7} xl={8}>
        <div className="d-flex justify-content-end gap-2 mt-1">
 {quotationTemplates.length > 0 && (activeValue === "Approved" || activeValue === "Sent" || activeValue === "Negotiate") && (
            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                size="sm"
                style={{
                  border: "1px solid #E2E8F0",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: 600,
                  marginRight: "8px",
                }}
              >
                {quotationTemplates.find((t) => t.id === selectedMailTemplateId)?.name ?? "Template"}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {quotationTemplates.map((template) => (
                  <Dropdown.Item
                    key={template.id}
                    onClick={() => handleSelectTemplate(template)}
                  >
                    {template.name}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}
          {(activeValue === "Approved" || activeValue === "Sent" || activeValue === "Negotiate") && (
            <div
              className="fs-14 fw-600 text-white d-flex justify-content-center align-items-center"
              style={{
                cursor: "pointer",
                minWidth: "108px",
                padding: "0 16px",
                height: "36px",
                borderRadius: "100px",
                backgroundColor: "#3D4AF5",
              }}
              onClick={() => {
                setEmailID(null);
                setTimeout(() => setEmailID(id), 0);
              }}
            >
              {activeValue === "Sent" ? "Resend mail" : "Send mail"}
            </div>
          )}
          {selectedMailTemplateId && (activeValue === "Approved" || activeValue === "Sent" || activeValue === "Negotiate") && (
            <div
              className="fs-14 fw-600 d-flex justify-content-center align-items-center"
              style={{
                cursor: previewingTemplateId === selectedMailTemplateId ? "not-allowed" : "pointer",
                minWidth: "108px",
                padding: "0 16px",
                height: "36px",
                borderRadius: "100px",
                border: "1px solid #3D4AF5",
                color: "#3D4AF5",
                opacity: previewingTemplateId === selectedMailTemplateId ? 0.6 : 1,
              }}
              onClick={previewingTemplateId === selectedMailTemplateId ? undefined : handleViewTemplatePdf}
            >
              {previewingTemplateId === selectedMailTemplateId ? "Loading..." : "View PDF"}
            </div>
          )}
          {selectedMailTemplateId && (activeValue === "Approved" || activeValue === "Sent" || activeValue === "Negotiate") && (
            <div
              className="fs-14 fw-600 text-white d-flex justify-content-center align-items-center gap-2"
              style={{
                cursor: "pointer",
                padding: "0 16px",
                height: "36px",
                borderRadius: "100px",
                backgroundColor: "#25D366",
              }}
              onClick={handleShareWhatsapp}
              title="Share on WhatsApp"
            >
              <FaWhatsapp size={16} />
              Share
            </div>
          )}
        </div>
          
         <div className="mt-2 d-flex justify-content-end gap-2">
          {quotation?.is_approved === true && quotation?.status === "Draft" && !isExpired && (
            <>
              {" "}
              <Button
                className={`cursor-pointer fs-14 fw-700   ${
                  loadingStatus ? "disabled-btn" : ""
                } border-0 rounded-2  py-2 px-4 d-flex justify-content-center align-items-center`}
                onClick={
                  !loadingStatus
                    ? () => {
                        updateStatus("Approved");
                      }
                    : () => {}
                }
                style={{
                  minWidth: "100px",
                  background: "#4EBD7E",
                }}
              >
                {loadingStatus === "Approved" ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Approve"
                )}
              </Button>
              <Button
                className={`cursor-pointer fs-14 fw-700   ${
                  loadingStatus ? "disabled-btn" : ""
                } border-0 rounded-2  py-2 px-4 d-flex justify-content-center align-items-center`}
                onClick={
                  !loadingStatus
                    ? () => {
                        updateStatus("Rejected");
                      }
                    : () => {}
                }
                style={{
                  minWidth: "100px",
                  background: "#FF4444",
                }}
              >
                {loadingStatus === "Rejected" ? <Spinner animation="border" size="sm" /> : "Reject"}
              </Button>
            </>
          )}
          {activeValue !== "Draft" && (
            <div className="d-flex">
              {steps.map((step, index) => (
                <div
                  key={step}
                  className="position-relative d-flex justify-content-center align-items-center"
                  style={{ marginLeft: "-10px", cursor: "pointer" }}
                  onClick={() => {
                    if (loadingStatus) return;
                    // Every step is once-only, except Negotiate — clicking
                    // it while already on Negotiate starts another
                    // negotiation round (a new child quotation), and stays
                    // clickable until the quotation is finally Customer
                    // Accepted/Rejected.
                    const isCurrentNegotiate =
                      activeStep === index && steps[index] === "Negotiate";
                    if (activeStep > index) return;
                    if (activeStep === index && !isCurrentNegotiate) return;
                    setActiveStep(index);
                    updateStatus(steps[index]);
                  }}
                >
                  {steps[index] === "Rejected" ||
                  steps[index] === "Customer Rejected" ? (
                    <SelectReject />
                  ) : (
                    getIcon(index, step)
                  )}

                  <span
                    style={{
                      marginLeft:
                        steps[index] === "Customer Rejected" ||
                        steps[index] === "Customer Accepted"||
                        steps[index] === "Sale Order"
                          ? "18px"
                          : "",
                    }}
                    className={`position-absolute fs-14 fw-600 ${
                      steps[index] === "Rejected" ||
                      steps[index] === "Customer Rejected"
                        ? "textRed"
                        : activeStep >= index
                          ? "textblue"
                          : "text-dark"
                    }`}
                  >
                    {step}
                  </span>
                </div>
              ))}
            </div>
          )}
          {activeValue === "Draft" && quotation?.is_approved === false && (
            <div className="d-flex">
              {DRAFT_STEPS.map((step, index) => (
                <div
                  key={step}
                  className="position-relative d-flex justify-content-center align-items-center"
                  style={{ marginLeft: "-10px", cursor: "pointer" }}
                  onClick={() => {
                    if (index < safeBackendStep) return;
                    setActiveStep(index);
                    updateStatus(steps[index]);
                  }}
                >
                  {steps[index] === "Rejected" ? (
                    <SelectReject />
                  ) : (
                    getIcon(index, step)
                  )}

                  <span
                    className={`position-absolute fs-14 fw-600 ${
                      steps[index] === "Rejected"
                        ? "textRed"
                        : activeStep >= index
                          ? "textblue"
                          : "text-dark"
                    }`}
                  >
                    {step}
                  </span>
                </div>
              ))}
            </div>
          )}

          
          {(activeValue === "Sent" || activeValue === "Negotiate") && (
            <>
              <Button
                className={`cursor-pointer fs-14 fw-700   ${
                  loadingStatus ? "disabled-btn" : ""
                } border-0 rounded-2  py-2 px-4 d-flex justify-content-center align-items-center`}
                onClick={
                  !loadingStatus
                    ? () => {
                        updateStatus("Customer Accepted");
                      }
                    : () => {}
                }
                style={{
                  minWidth: "160px",
                  background: "#4EBD7E",
                }}
              >
                {loadingStatus === "Customer Accepted" ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Customer Accepted"
                )}
              </Button>
              <Button
                className={`cursor-pointer fs-14 fw-700   ${
                  loadingStatus ? "disabled-btn" : ""
                } border-0 rounded-2  py-2 px-4 d-flex justify-content-center align-items-center`}
                onClick={
                  !loadingStatus
                    ? () => {
                        updateStatus("Customer Rejected");
                      }
                    : () => {}
                }
                style={{
                  minWidth: "160px",
                  background: "#FF4444",
                }}
              >
                {loadingStatus === "Customer Rejected" ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Customer Rejected"
                )}
              </Button>
            </>
          )}
          {activeValue === "Customer Accepted" && (
            <Button
              className={`cursor-pointer fs-14 fw-700   ${
                loadingStatus ? "disabled-btn" : ""
              } border-0 rounded-2  py-2 px-4 d-flex justify-content-center align-items-center`}
              onClick={
                !loadingStatus
                  ? () => {
                      updateStatus("Sale Order");
                    }
                  : () => {}
              }
              style={{
                minWidth: "160px",
                background: "#3D4AF5",
              }}
            >
              {loadingStatus === "Sale Order" ? (
                <Spinner animation="border" size="sm" />
              ) : (
                "Convert to Sale Order"
              )}
            </Button>
          )}
          {activeValue === "Draft" && hasEditPermission && (
            <div
              className="fs-14 fw-600 text-dkblue d-flex justify-content-center align-items-center"
              onClick={(e) => {
                e.stopPropagation();
                navigate("/quotation/create", {
                  state: { id: id, fromView: true },
                });
              }}
              style={{
                cursor: "pointer",
                width: "82px",
                height: "36px",
                borderRadius: "100px",
                border: " 1px solid #D4DDE7",
              }}
              title="Edit Quotation"
            >
              Edit
            </div>
          )}
          {activeValue === "Draft" && hasDeletePermission && (
            <div
              className="fs-14 fw-600 text-dkblue d-flex justify-content-center align-items-center"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteId(id);
              }}
              style={{
                cursor: "pointer",
                width: "36px",
                height: "36px",
                borderRadius: "100px",
                border: " 1px solid #D4DDE7",
                marginRight: "16px",
              }}
              title="Delete Quotation"
            >
              <DeleteRow />
            </div>
          )}
          </div>
        </Col>
      </Row>
      {/* Expired Quotation Banner */}
      {isExpired && (
        <div
          style={{
            margin: "16px 16px 0 16px",
            padding: "14px 20px",
            backgroundColor: "#FFF4E5",
            border: "1px solid #FFE2B8",
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            boxShadow: "0 2px 6px rgba(230, 81, 0, 0.08)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <FaClock size={20} style={{ color: "#E65100", flexShrink: 0 }} />
            <div>
              <div style={{ color: "#E65100", fontSize: "14px", fontWeight: 700 }}>
                This quotation is expired
              </div>
              <div style={{ color: "#8D4004", fontSize: "13px", marginTop: "2px" }}>
                The validity date ({quotation?.valid_date ? new Date(quotation.valid_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "N/A"}) has passed. Please edit the quotation to update its validity date before approving.
              </div>
            </div>
          </div>
          {hasEditPermission && (
            <Button
              variant="outline-warning"
              size="sm"
              style={{
                borderColor: "#E65100",
                color: "#E65100",
                fontWeight: 600,
                borderRadius: "8px",
                padding: "6px 16px",
                whiteSpace: "nowrap",
                backgroundColor: "#FFFFFF",
              }}
              onClick={() => setShowExtendModal(true)}
            >
              Extend Validity
            </Button>
          )}
        </div>
      )}
      <Row style={{ padding: "16px" }}>
        <Col sm={12} lg={3}>
          <div className="q-customer-card p-4">
            {/* -------- Customer Details -------- */}
            <div className="fs-16 fw-600 text-dkblue mb-3">
              {quotation?.type} details
            </div>

            <div className="q-detail-row">
              <MailIC />
              <span
                className="fs-15 fw-400  text-dkblue"
                style={{ marginLeft: "10px" }}
              >
                {quotation?.contact_person_email ?? "--"}
              </span>
            </div>

            <div className="q-detail-row">
              <CallIc />
              <span
                className="fs-15 fw-400  text-dkblue"
                style={{ marginLeft: "10px", marginTop: "6px" }}
              >
                {quotation?.phone ?? "--"}
              </span>
            </div>

            {quotation?.company && (
              <div className="q-detail-row">
                <BuildIc />
                <span
                  className="fs-15 fw-400  text-dkblue"
                  style={{ marginLeft: "10px", marginTop: "6px" }}
                >
                  {quotation?.company ?? "-"}
                </span>
              </div>
            )}

            {
              <div className="q-detail-row">
                <ITICs />
                <span
                  className="fs-15 fw-400  text-dkblue"
                  style={{ marginLeft: "10px", marginTop: "6px" }}
                >
                  {quotation?.industry ?? "-"}
                </span>
              </div>
            }

            <div className="q-detail-row mb-4">
              <LocatIc />
              <span
                className="fs-15 fw-400  text-dkblue"
                style={{ marginLeft: "10px" }}
              >
                {quotation?.bill_city ?? "-"}
              </span>
            </div>
            <div className="q-detail-row mb-4">
              <PersonIC />
              <span
                className="fs-15 fw-400  text-dkblue"
                style={{ marginLeft: "10px" }}
              >
                {quotation?.contact_person ?? "-"}
              </span>
            </div>

            <div className="divider"></div>

            {/* -------- Sales Person -------- */}

            <div className="fs-16 fw-600 text-dkblue mb-3 mt-3">
              Sale Person
            </div>
            <div className="sales-row align-items-start">
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <img
                  src={ProfileIc}
                  alt="Profile"
                  className="profile-img"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "50%",
                  }}
                />
              </div>

              <div className="ms-3">
                <div className="fs-15 fw-600 text-dkblue">
                  {" "}
                  {quotation?.sales_person ?? "-"}
                </div>

                <div className="q-detail-row mt-1">
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ width: 14, height: 14 }}
                  >
                    <MailIC />
                  </div>
                  <span className="fs-14 fw-400 text-dkblue ms-2">
                    {quotation?.sales_person_email ?? "-"}
                  </span>
                </div>
              </div>
            </div>
            {/* <div className="fs-16 fw-600 text-dkblue mb-3 mt-3">
              Contact Person
            </div> */}

            {/* <div className="sales-row align-items-start">
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <img
                  src={ProfileIc}
                  alt="Profile"
                  className="profile-img"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "50%",
                  }}
                />
              </div>

              <div className="ms-3">
                <div className="fs-15 fw-600 text-dkblue">
            
                  {quotation?.contact_person ?? "-"}
                </div>

                <div className="q-detail-row mt-1">
                  {quotation?.phone && (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ width: 14, height: 14 }}
                    >
                      <CallIc />
                    </div>
                  )}
                  {quotation?.phone && (
                    <span className="fs-14 fw-400 text-dkblue ms-2">
                      {quotation?.phone ?? "-"}
                    </span>
                  )}{" "}
                  <div
                    style={{
                      width: "4px",
                      height: "4px",
                      marginLeft: "6px",
                      marginRight: "6px",
                      borderRadius: "50%",
                      backgroundColor: "#D9D9D9",
                    }}
                  ></div>
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ width: 14, height: 14 }}
                  >
                    <MailIC />
                  </div>
                  <span className="fs-14 fw-400 text-dkblue ms-2">
                    {quotation?.contact_person_email ?? "-"}
                  </span>
                </div>
     
              </div>
            </div> */}

            <div className="divider"></div>

            {/* -------- Subject -------- */}
            <div className="fs-16 fw-600 text-dkblue mt-3">Title</div>
            <p className="fs-15 fw-400 text-dkblue">
              {quotation?.title ?? "-"}
            </p>
            <div className="divider"></div>
            <div className="fs-16 fw-600 text-dkblue mt-3">Subject</div>
            <p className="fs-15 fw-400 text-dkblue">
              {quotation?.subject ?? "-"}
            </p>

            <div className="divider"></div>

            {/* -------- Description -------- */}
            <div className="fs-16 fw-600 text-dkblue mt-3">Description</div>
            <p className="fs-15 fw-400 text-dkblue">
              {quotation?.description ?? "-"}
            </p>
            <div className="fs-16 fw-600 text-dkblue mt-3">Created at</div>
            <p className="fs-15 fw-400 text-dkblue">
              {quotation?.created_date ?? "-"} at{" "}
              {quotation?.created_time ?? "-"} by {quotation?.created_by ?? "-"}
            </p>
          </div>
        </Col>
        <Col sm={12} lg={9}>
          {!isQuotView && (
            <>
              <div className="tabs-wrapper">
                <div
                  className={`tab-item ${
                    active === "details" ? "active" : ""
                  } fs-14 fw-600 `}
                  onClick={() => setActive("details")}
                >
                  Quotation Details
                </div>

                <div
                  className={`tab-item ${
                    active === "attachments" ? "active" : ""
                  } fs-14 fw-600 `}
                  onClick={() => setActive("attachments")}
                >
                  Attachments ({attachments.length})
                </div>

                <div
                  className={`tab-item ${
                    active === "history" ? "active" : ""
                  } fs-14 fw-600 `}
                  onClick={() => setActive("history")}
                >
                  Quotation History
                </div>

                <div
                  className={`tab-item ${
                    active === "workflow" ? "active" : ""
                  } fs-14 fw-600 `}
                  onClick={() => setActive("workflow")}
                >
                  Workflow
                </div>

                <div
                  className={`tab-item ${
                    active === "ai" ? "active" : ""
                  } fs-14 fw-600 `}
                  onClick={() => setActive("ai")}
                >
                  AI Summary
                </div>
              </div>
              {active === "ai" && (
                <AiSummaryCard
                  api={getQuotationAiSummaryApi}
                  module="quotation"
                  id={id}
                  initial={quotation}
                  hint="Score this quotation on how likely it is to convert — line-by-line price history, the customer's conversion record and what they owe — with a suggested next action."
                />
              )}
              {active === "details" && (
                <div>
                  <div className="d-flex gap-3 mt-2">
                    <div
                      className="flex-fill bg-white"
                      style={{
                        minWidth: "150px",
                        maxWidth: "250px",
                        height: "60px",

                        flex: "1",
                      }}
                    >
                      <div
                        className={` bg-white h-100 `}
                        style={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "start",
                          textAlign: "start",
                        }}
                      >
                        <div
                          className="card-body d-flex align-items-center justify-content-start"
                          style={{ paddingLeft: "12px" }}
                        >
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "8px",
                              backgroundColor: "#F7F8F9",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              marginRight: "10px", // space between icon and text
                            }}
                          >
                            <QuotaIC />
                          </div>
                          <div className="d-flex flex-column align-items-start">
                            <div className="fw-600 fs-10 text-grey mb-2">
                              QUOTATION ID
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px", // space between number and dot
                              }}
                            >
                              <span className="fw-600 fs-15 textcolor">
                                {quotation?.quotation_id ?? "--"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      className="flex-fill bg-white"
                      style={{
                        minWidth: "150px",
                        maxWidth: "200px",
                        height: "60px",

                        flex: "1",
                      }}
                    >
                      <div
                        className={` bg-white h-100 `}
                        style={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "start",
                          textAlign: "start",
                        }}
                      >
                        <div
                          className="card-body d-flex align-items-center justify-content-start"
                          style={{ paddingLeft: "12px" }}
                        >
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "8px",
                              backgroundColor: "#F7F8F9",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              marginRight: "10px", // space between icon and text
                            }}
                          >
                            <QuotaIC />
                          </div>
                          <div className="d-flex flex-column align-items-start">
                            <div className="fw-600 fs-10 text-grey mb-2">
                              VALIDITY DATE
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px", // space between number and dot
                              }}
                            >
                              <span className="fw-600 fs-15 textcolor">
                                {quotation?.valid_date ?? "--"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      className="flex-fill bg-white"
                      style={{
                        minWidth: "150px",
                        maxWidth: "200px",
                        height: "60px",

                        flex: "1",
                      }}
                    >
                      <div
                        className={` bg-white h-100 `}
                        style={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "start",
                          textAlign: "start",
                        }}
                      >
                        <div
                          className="card-body d-flex align-items-center justify-content-start"
                          style={{ paddingLeft: "12px" }}
                        >
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              borderRadius: "8px",
                              backgroundColor: "#F7F8F9",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              marginRight: "10px", // space between icon and text
                            }}
                          >
                            <QuotaIC />
                          </div>
                          <div className="d-flex flex-column align-items-start">
                            <div className="fw-600 fs-10 text-grey mb-2">
                              AMOUNT
                            </div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px", // space between number and dot
                              }}
                            >
                              <span className="fw-600 fs-15 textcolor">
                                {currencySymbol} {quotation?.total_amount ?? "--"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {quotation?.currency_name && (
                      <div
                        className="flex-fill bg-white"
                        style={{
                          minWidth: "150px",
                          maxWidth: "200px",
                          height: "60px",
                          flex: "1",
                        }}
                      >
                        <div
                          className={` bg-white h-100 `}
                          style={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "start",
                            textAlign: "start",
                          }}
                        >
                          <div
                            className="card-body d-flex align-items-center justify-content-start"
                            style={{ paddingLeft: "12px" }}
                          >
                            <div
                              style={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "8px",
                                backgroundColor: "#F7F8F9",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                marginRight: "10px",
                              }}
                            >
                              <QuotaIC />
                            </div>
                            <div className="d-flex flex-column align-items-start">
                              <div className="fw-600 fs-10 text-grey mb-2">
                                CURRENCY
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <span className="fw-600 fs-15 textcolor">
                                  {currencySymbol} {quotation?.currency_name}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {quotation?.exchange_rate != null &&
                      quotation?.exchange_rate !== "" &&
                      quotation?.organization_currency_id &&
                      quotation?.currency_id &&
                      quotation.organization_currency_id !== quotation.currency_id && (
                      <div
                        className="flex-fill bg-white"
                        style={{
                          minWidth: "150px",
                          maxWidth: "200px",
                          height: "60px",
                          flex: "1",
                        }}
                      >
                        <div
                          className={` bg-white h-100 `}
                          style={{
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "start",
                            textAlign: "start",
                          }}
                        >
                          <div
                            className="card-body d-flex align-items-center justify-content-start"
                            style={{ paddingLeft: "12px" }}
                          >
                            <div
                              style={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "8px",
                                backgroundColor: "#F7F8F9",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                marginRight: "10px",
                              }}
                            >
                              <QuotaIC />
                            </div>
                            <div className="d-flex flex-column align-items-start">
                              <div className="fw-600 fs-10 text-grey mb-2">
                                EXCHANGE RATE
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <span className="fw-600 fs-15 textcolor">
                                  {quotation?.exchange_rate}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {quotation?.pdf_view && (
                      <div
                        className="flex-fill d-flex justify-content-center align-items-center"
                        style={{
                          minWidth: "150px",
                          maxWidth: "200px",
                          height: "60px",
                          flex: "1",
                        }}
                        onClick={() => {
                          setTemplatePreviewUrl(null);
                          setSelectedMailTemplateId(null);
                          setIsQuotView(!isQuotView);
                        }}
                      >
                        <EyeIc />
                        <span
                          className="fs-13 fw-400 textblue ms-2"
                          style={{
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          Pdf view
                        </span>
                      </div>
                    )}
                  </div>
                  {quotation?.project && (
                    <div className="d-flex mt-2">
                      <div
                        className="flex-fill bg-white"
                        style={{
                          height: "137px",
                          flex: "1",
                          padding: "12px",
                        }}
                      >
                        {" "}
                        <div className="fs-16 fw-600 text-dkblue mb-3">
                          Project Details
                        </div>
                        <div
                          style={{
                            width: "100%",
                            borderRadius: "16px",
                            backgroundColor: "#F3F4FF",
                            height: "69px",

                            display: "flex", // ⭐ Make parent a flex container
                            justifyContent: "start", // ⭐ Center horizontally
                            alignItems: "center", // ⭐ Center vertically
                          }}
                        >
                          <div
                            style={{
                              width: "40px",
                              height: "40px",
                              backgroundColor: "#ffffff",
                              borderRadius: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              marginLeft: "12px",
                            }}
                          >
                            <QuotaIC1 />
                          </div>
                          <div
                            className="d-flex flex-column"
                            style={{ marginLeft: "12px" }}
                          >
                            <div className="fs-16 fw-500 text-dkblue">
                              {quotation?.project ?? "--"}
                            </div>

                            <div className="mt-1">
                              <span className="fs-12 fw-400 text-grey">
                                Rate :
                              </span>
                              <span className="fs-12 fw-500 textblue">
                                {" "}
                                {currencySymbol} {quotation?.project_value ?? "--"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="d-flex mt-2">
                    <div
                      className="flex-fill bg-white"
                      style={{
                        // height: "137px",
                        flex: "1",
                        padding: "12px",
                      }}
                    >
                      {" "}
                      <div className="fs-16 fw-600 text-dkblue mb-3">
                        Address Information
                      </div>
                      <Row>
                        <Col sm={12} lg={5}>
                          <div
                            style={{
                              width: "44px",
                              height: "44px",
                              borderRadius: "100%",
                              backgroundColor: "#FFE8E8",
                              display: "flex", // ⭐ enable flexbox
                              justifyContent: "center", // ⭐ center horizontally
                              alignItems: "center", // ⭐ center vertically
                            }}
                          >
                            <LocsIC />
                          </div>
                          <div className="fs-11 fw-600 text-grey mt-2">
                            BILLING ADDRESS
                          </div>
                          <div className="fs-14 fw-400 text-grey1 mt-1">
                            {formatAddress(quotation ?? "") ?? ""}
                          </div>
                        </Col>
                        <Col
                          sm={12}
                          lg={2}
                          className="d-flex justify-content-center"
                        >
                          <div
                            style={{
                              width: "1px",
                              height: "80%",
                              backgroundColor: "#E5E5E5",
                            }}
                          ></div>
                        </Col>

                        <Col sm={12} lg={5}>
                          <div
                            style={{
                              width: "44px",
                              height: "44px",
                              borderRadius: "100%",
                              backgroundColor: "#FFE8E8",
                              display: "flex", // ⭐ enable flexbox
                              justifyContent: "center", // ⭐ center horizontally
                              alignItems: "center", // ⭐ center vertically
                            }}
                          >
                            <LocsIC />
                          </div>
                          <div className="fs-11 fw-600 text-grey mt-2">
                            SHIPPING ADDRESS
                          </div>
                          <div className="fs-14 fw-400 text-grey1 mt-1">
                            {formatShipAddress(quotation ?? "")}
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                  <div className="d-flex mt-2">
                    <div
                      className="flex-fill bg-white"
                      style={{
                        // height: "137px",
                        flex: "1",
                        padding: "12px",
                      }}
                    >
                      <div className="fs-16 fw-600 text-dkblue mb-3">
                        Products
                      </div>
                      <div
                        style={{
                          backgroundColor: "#F7F8FC",
                          display: "grid",
                          gridTemplateColumns: `
                          0.8fr     /* HSN CODE */
      1.5fr     /* PRODUCT NAME */      
      0.8fr     /* UNIT */
      0.8fr     /* QUANTITY */
      1fr       /* UNIT PRICE */
      1fr       /* AMOUNT */
      ${hasDiscount ? "1fr" : ""} /* DISCOUNT */
      ${isCgst ? "0.6fr" : ""}
      ${isSgst ? "0.6fr" : ""}
      ${isIgst ? "0.6fr" : ""}
      1fr       /* TOTAL AMOUNT */
    `,
                          padding: "10px 16px",
                          gap: "10px",
                          alignItems: "center",
                          borderBottom: "1px solid #EBEBEB",
                          width: "100%",
                        }}
                      >
                        <div className="fs-11 fw-600 textGRE">HSN </div>
                        <div className="fs-11 fw-600 textGRE">DESCRIPTION</div>
                        <div className="fs-11 fw-600 textGRE">UNIT</div>
                        <div className="fs-11 fw-600 textGRE">QUANTITY</div>
                        <div className="fs-11 fw-600 textGRE">UNIT PRICE</div>
                        <div className="fs-11 fw-600 textGRE">AMOUNT</div>
                        {hasDiscount && (
                          <div className="fs-11 fw-600 textGRE">DISCOUNT</div>
                        )}
                        {isCgst && (
                          <div className="fs-11 fw-600 textGRE">CGST %</div>
                        )}
                        {isSgst && (
                          <div className="fs-11 fw-600 textGRE">SGST %</div>
                        )}
                        {isIgst && (
                          <div className="fs-11 fw-600 textGRE">IGST %</div>
                        )}
                        <div className="fs-11 fw-600 textGRE">TOTAL</div>
                      </div>
                      {productList.map((row, index) => {
                        return (
                          <div
                            key={index}
                            style={{
                              borderBottom: "1px solid #EBEBEB",
                              paddingBottom: "6px",
                            }}
                          >
                            <div
                              key={index}
                              style={{
                                display: "grid",
                                gridTemplateColumns: `                                
      0.8fr     /* HSN CODE */
      1.5fr     /* PRODUCT NAME */
      0.8fr     /* UNIT */
      0.8fr     /* QUANTITY */
      1fr       /* UNIT PRICE */
      1fr       /* AMOUNT */
      ${hasDiscount ? "1fr" : ""} /* DISCOUNT */
      ${isCgst ? "0.6fr" : ""}
      ${isSgst ? "0.6fr" : ""}
      ${isIgst ? "0.6fr" : ""}
      1fr       /* TOTAL AMOUNT */
    `,

                                padding: "10px 16px",
                                gap: "10px",
                                alignItems: "center",
                              }}
                            >
                              <div className="fs-11 fw-600 textGRE">
                                {row.hsn_no || "-"}
                              </div>
                              <div className="fs-11 fw-600 textGRE">
                                {row.product_name ?? "-"}
                              </div>
                              
                              <div className="fs-11 fw-600 textGRE">
                                {" "}
                                {row.uom || "-"}
                              </div>
                              <div className="fs-11 fw-600 textGRE">
                                {row.quantity ?? "-"}
                              </div>
                              <div className="fs-11 fw-600 textGRE">
                                {row.unit_price ?? "-"}
                              </div>
                              <div className="fs-11 fw-600 textGRE">
                                {getItemBreakdown(row).baseAmount.toLocaleString("en-IN", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </div>
                              {hasDiscount && (
                                <div className="fs-11 fw-600 textGRE">
                                  {getItemBreakdown(row).itemDiscount.toLocaleString("en-IN", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </div>
                              )}
                              {isCgst && (
                                <div className="fs-11 fw-600 textGRE">
                                  {row.cgst_percentage ?? "-"}
                                </div>
                              )}
                              {isSgst && (
                                <div className="fs-11 fw-600 textGRE">
                                  {row.sgst_percentage ?? "-"}
                                </div>
                              )}
                              {isIgst && (
                                <div className="fs-11 fw-600 textGRE">
                                  {row.igst_percentage ?? "-"}
                                </div>
                              )}
                              {(() => {
                                const bd = getItemBreakdown(row);
                                const rows = [
                                  ["Amount", money(bd.baseAmount)],
                                  ...(bd.itemDiscount > 0
                                    ? [["Discount", `- ${money(bd.itemDiscount)}`]]
                                    : []),
                                  ["Taxable", money(bd.taxableAmount)],
                                  ...(bd.cgst > 0
                                    ? [[`CGST (${bd.cgst}%)`, `+ ${money(bd.cgstAmount)}`]]
                                    : []),
                                  ...(bd.sgst > 0
                                    ? [[`SGST (${bd.sgst}%)`, `+ ${money(bd.sgstAmount)}`]]
                                    : []),
                                  ...(bd.igst > 0
                                    ? [[`IGST (${bd.igst}%)`, `+ ${money(bd.igstAmount)}`]]
                                    : []),
                                  ["Total", money(bd.total), true],
                                ];
                                return (
                                  <div className="fs-11 fw-600 textGRE d-flex align-items-center gap-1">
                                    <span>
                                      {bd.total.toLocaleString("en-IN", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                    <InfoTooltip rows={rows} />
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        );
                      })}
                      <Row>
                        <Col sm={12} lg={5}>
                          <div className="mt-4">
                            <div className="fs-13 fw-400 text-grey ">
                              Terms and Conditions
                            </div>
                            <div className="fs-13 fw-400 text-dkblue mt-2">
                              {quotation?.terms ?? "-"}
                            </div>
                          </div>
                          <div className="mt-2">
                            <div className="fs-13 fw-400 text-grey ">
                              Customer Notes
                            </div>
                            <div className="fs-13 fw-400 text-dkblue mt-1">
                              {quotation?.notes ?? "-"}
                            </div>
                          </div>
                        </Col>
                        <Col sm={12} lg={7}>
                          <div className=" d-flex flex-column gap-1 mt-4">
                            <div
                              style={{
                                width: "100%",
                                backgroundColor: "#F8F8F8",
                                borderRadius: "12px",
                                padding: "10px",
                                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
                                marginLeft: "12px",
                                height: "100%",
                              }}
                            >
                              <div className="row-item">
                                <span className="fs-13 fw-400 textblk">
                                  Sub Total
                                </span>
                                <span className="fs-14 fw-600 text-dkblue">
                                  {currencySymbol} {quotation?.sub_total ?? "-"}
                                </span>
                              </div>
                              <div className="divider"></div>
                              {/* Discount */}
                              <div className="tds-row">
                                <div className="tds-options">
                                  <span className="fs-13 fw-400 textblk d-inline-flex align-items-center gap-1">
                                    Discount
                                    {hasDiscount && (
                                      <InfoTooltip
                                        rows={[
                                          ["On amount", money(quotation?.sub_total)],
                                          ["Discount amount", `- ${money(totalDiscount)}`],
                                          ...(productList.length > 0
                                            ? [
                                                [
                                                  `Per item (÷${productList.length})`,
                                                  `- ${money(itemDiscount)}`,
                                                ],
                                              ]
                                            : []),
                                        ]}
                                      />
                                    )}
                                  </span>
                                </div>
                                <span className="fs-14 fw-600 text-dkblue">
                                  {totalDiscount > 0 ? "- " : ""}
                                  {money(totalDiscount)}
                                </span>
                              </div>

                              {/* GST */}
                              {totalGst > 0 && (
                                <div className="tds-row">
                                  <div className="tds-options">
                                    <span className="fs-13 fw-400 textblk d-inline-flex align-items-center gap-1">
                                      GST
                                      <InfoTooltip
                                        rows={[
                                          [
                                            "On amount",
                                            money(toNum(quotation?.sub_total) - totalDiscount),
                                          ],
                                          ...(gstTotals.cgst > 0
                                            ? [["CGST", money(gstTotals.cgst)]]
                                            : []),
                                          ...(gstTotals.sgst > 0
                                            ? [["SGST", money(gstTotals.sgst)]]
                                            : []),
                                          ...(gstTotals.igst > 0
                                            ? [["IGST", money(gstTotals.igst)]]
                                            : []),
                                          ["Total GST", money(totalGst), true],
                                        ]}
                                      />
                                    </span>
                                  </div>
                                  <span className="fs-14 fw-600 text-dkblue">
                                    {money(totalGst)}
                                  </span>
                                </div>
                              )}

                              

                              {/* TDS / TCS */}
                             
                              {/* Adjustments */}
                              <div className="tds-row">
                                <div className="tds-options">
                                  <span className="fs-13 fw-400 textblk d-inline-flex align-items-center gap-1">
                                    Adjustments
                                    <InfoTooltip
                                      rows={[
                                        [
                                          "Adjustment",
                                          `${toNum(quotation?.adjustments) < 0 ? "- " : ""}${money(Math.abs(toNum(quotation?.adjustments)))}`,
                                        ],
                                        ["Applied to", "Grand total"],
                                      ]}
                                    />
                                  </span>
                                </div>
                                <span className="fs-14 fw-600 text-dkblue">
                                  {toNum(quotation?.adjustments) < 0 ? "- " : ""}
                                  {money(Math.abs(toNum(quotation?.adjustments)))}
                                </span>
                              </div>
                              <div className="divider"></div>
                              <div className="row-item total">
                                <span className="fs-15 fw-600 text-dkblue">
                                  Total Amount
                                </span>
                                <span className="fs-16 fw-600 text-dkblue">
                                  {currencySymbol} {quotation?.total_amount ?? "-"}
                                </span>
                              </div>

                              {/* Organization currency differs from the quotation/customer
                                  currency — show the base-currency equivalent
                                  (Total Amount × Exchange Rate). */}
                              {quotation?.organization_currency_id &&
                                quotation?.currency_id &&
                                quotation.organization_currency_id !== quotation.currency_id && (
                                  <div className="row-item">
                                    <span className="fs-13 fw-400 textblk d-inline-flex align-items-center gap-1">
                                      Total in {orgCurrencyName || "Org Currency"}
                                      <InfoTooltip
                                        rows={[
                                          ["Total Amount", money(toNum(quotation?.total_amount))],
                                          ["Exchange Rate", `× ${toNum(quotation?.exchange_rate) || 1}`],
                                          [
                                            "Converted Amount",
                                            `${orgCurrencySymbol} ${(toNum(quotation?.total_amount) * (toNum(quotation?.exchange_rate) || 1)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
                                            true,
                                          ],
                                        ]}
                                      />
                                    </span>
                                    <span className="fs-14 fw-600 text-dkblue">
                                      {orgCurrencySymbol} {(toNum(quotation?.total_amount) * (toNum(quotation?.exchange_rate) || 1)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                )}
                               {quotation?.tds_amount > 0 && (
                                <div className="tds-row">
                                  <div className="tds-options">
                                    <span className="fs-13 fw-400 textblk d-inline-flex align-items-center gap-1">
                                      TDS Amount
                                      <InfoTooltip
                                        rows={[
                                          [
                                            "On amount",
                                            money(toNum(quotation?.sub_total) - totalDiscount),
                                          ],
                                          ["Rate", `${toNum(quotation?.tds_percentage)}%`],
                                          ["TDS", `- ${money(quotation?.tds_amount)}`, true],
                                        ]}
                                      />
                                    </span>
                                  </div>
                                  <span className="fs-14 fw-600 text-dkblue">
                                    {toNum(quotation?.tds_amount) > 0 ? "- " : ""}
                                    {money(quotation?.tds_amount)}
                                  </span>
                                </div>
                              )}
                              {quotation?.tcs_amount > 0 && (
                                <div className="tds-row">
                                  <div className="tds-options">
                                    <span className="fs-13 fw-400 textblk d-inline-flex align-items-center gap-1">
                                      TCS Amount
                                      <InfoTooltip
                                        rows={[
                                          [
                                            "On amount",
                                            money(toNum(quotation?.sub_total) - totalDiscount),
                                          ],
                                          ["Rate", `${toNum(quotation?.tcs_percentage)}%`],
                                          ["TCS", `+ ${money(quotation?.tcs_amount)}`, true],
                                        ]}
                                      />
                                    </span>
                                  </div>
                                  <span className="fs-14 fw-600 text-dkblue">
                                    {currencySymbol} {quotation?.tcs_amount ?? "0"}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              )}
              {active === "attachments" && (
                <div
                  className="q-customer-card mt-3"
                  style={{ padding: "24px" }}
                >
                  <div className="fs-16 fw-600 text-dkblue mb-4">
                    Attachments
                  </div>
                  {attachments.length > 0 ? (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "16px" }}>
                      {attachments.map((attachment, idx) => (
                        <div
                          key={idx}
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: "12px",
                            padding: "16px",
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                            backgroundColor: "#f9fafb",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";
                            e.currentTarget.style.transform = "translateY(-2px)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = "none";
                            e.currentTarget.style.transform = "translateY(0)";
                          }}
                        >
                          <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                          }}>
                            <div style={{
                              width: "48px",
                              height: "48px",
                              borderRadius: "8px",
                              backgroundColor: "#10b981",
                              color: "white",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "11px",
                              fontWeight: "700",
                              flexShrink: 0,
                            }}>
                              {attachment.file_name.split(".").pop().toUpperCase()}
                            </div>
                            <div style={{
                              flex: 1,
                              minWidth: 0,
                              display: "flex",
                              flexDirection: "column",
                              gap: "4px",
                            }}>
                              <div style={{
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "#1f2937",
                                wordBreak: "break-word",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}>
                                {attachment.file_name}
                              </div>
                              <div style={{
                                fontSize: "12px",
                                color: "#9ca3af",
                              }}>
                                {attachment.file_size} MB
                              </div>
                            </div>
                          </div>

                          <div style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "flex-end",
                          }}>
                            {isPreviewableFile(attachment.file_name) && (
                              <button
                                onClick={() => handlePreviewFile(attachment)}
                                title={isPdfFile(attachment.file_name) ? "Preview PDF" : "Preview image"}
                                style={{
                                  padding: "8px 12px",
                                  backgroundColor: "#f3f4f6",
                                  border: "1px solid #d1d5db",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                  fontSize: "12px",
                                  transition: "all 0.2s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "#e5e7eb";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "#f3f4f6";
                                }}
                              >
                                <FaEye size={14} style={{ color: "#3b82f6" }} />
                                Preview
                              </button>
                            )}
                            <button
                              onClick={() => handleDownloadAttachment(attachment)}
                              title="Download"
                              style={{
                                padding: "8px 12px",
                                backgroundColor: "#dbeafe",
                                border: "1px solid #93c5fd",
                                borderRadius: "6px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                fontSize: "12px",
                                textDecoration: "none",
                                color: "#1e40af",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#bfdbfe";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "#dbeafe";
                              }}
                            >
                              <FaDownload size={14} />
                              Download
                            </button>
                            <button
                              onClick={() =>
                                handleDeleteAttachment(attachment.id, attachment.file_name)
                              }
                              title="Delete"
                              style={{
                                padding: "8px 12px",
                                backgroundColor: "#fee2e2",
                                border: "1px solid #fecaca",
                                borderRadius: "6px",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                fontSize: "12px",
                                color: "#dc2626",
                                transition: "all 0.2s ease",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "#fca5a5";
                                e.currentTarget.style.color = "#991b1b";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "#fee2e2";
                                e.currentTarget.style.color = "#dc2626";
                              }}
                            >
                              <FaTrash size={14} />
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "40px",
                        color: "#6b7280",
                      }}
                    >
                      <div style={{ fontSize: "16px", fontWeight: "500", marginBottom: "8px" }}>
                        No attachments
                      </div>
                      <div style={{ fontSize: "14px" }}>
                        There are no attachments for this quotation yet.
                      </div>
                    </div>
                  )}
                </div>
              )}
              {active === "history" && (
                <div
                  className="q-customer-card mt-3"
                  style={{ padding: "16px" }}
                >
                  <div className="fs-16 fw-600 text-dkblue">
                    Quotation history
                  </div>{" "}
                  <div
                    style={{
                     /*  maxHeight: "300px", */ // adjust as needed (e.g., 250px, 400px)
                      overflowY: "auto",
                      overflowX: "hidden",
                      paddingRight: "8px", // small spacing for scrollbar
                    }}
                    className="scroll-container"
                  >
                    <div
                      className="d-flex flex-column ms-3"
                      style={{ padding: "8px" }}
                    >
                      {quotHistory.slice().reverse().map((txn, idx) => (
                        <div
                          key={idx}
                          className="d-flex mb-4 position-relative"
                        >
                          {/* Green tick */}
                          <div
                            className="d-flex align-items-center justify-content-center rounded-circle"
                            style={{
                              backgroundColor: "#D1D5FF",
                              width: "40px",
                              height: "40px",
                              marginRight: "15px",
                              marginLeft: "-18px",
                              zIndex: "1",
                            }}
                          >
                            <div
                              className="d-flex align-items-center justify-content-center rounded-circle"
                              style={{
                                backgroundColor: "#D1D5FF",
                                width: "40px",
                                height: "40px",
                                // marginRight: "15px",
                              }}
                            >
                              <HistoryIc />
                            </div>
                          </div>

                          {/* Vertical dashed line */}
                          {idx !== quotHistory.length - 1 && (
                            <div
                              className="position-absolute start-0"
                              style={{
                                borderLeft: "2px dashed #3469E7",
                                height: "calc(100% + 16px)",
                                left: "8px",
                                top: "10px",
                              }}
                            />
                          )}

                          {/* Transaction details */}
                          <div className="flex-grow-1 d-flex justify-content-between align-items-start">
                            <div>
                            <div className="d-flex align-items-center mb-1">
                              <span className="fw-500 fs-14 text-grey me-2">
                                ID :
                              </span>
                              <span className="fw-500 fs-14 text-dkblue me-2">
                                {txn.quotation_code ?? ""}
                              </span>
                              {String(txn.quotation_id) !== String(id) && (
                                <div
                                  onClick={() => {
                                    navigate(
                                      `/quotation/view/${txn.quotation_id}`,
                                      {
                                        state: { refresh: Date.now() },
                                      },
                                    );
                                    // navigate(0) causes full reload — usually not needed
                                  }}
                                  style={{
                                    cursor: "pointer",
                                    width: "20px",
                                    height: "20px",
                                    borderRadius: "100px",
                                    backgroundColor: "#E5E7FF",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <RightArrow />
                                </div>
                              )}
                              {/* <span className="fw-500 fs-14 text-dkblue me-2">
                                {txn.status ?? 0}
                              </span> */}
                            </div>

                            {txn.status === "Converted to Sale Order" && txn.sale_order_code && (
                              <div className="d-flex align-items-center mb-1">
                                <span className="fw-500 fs-14 text-grey me-2">
                                  Sale Order :
                                </span>
                                <span
                                  className="fw-500 fs-14 textblue me-2"
                                  style={{ cursor: "pointer", textDecoration: "underline" }}
                                  onClick={() =>
                                    navigate("/order/view", {
                                      state: { id: txn.sale_order_id },
                                    })
                                  }
                                >
                                  {txn.sale_order_code}
                                </span>
                              </div>
                            )}

                            <div className="d-flex align-items-center mb-1 flex-wrap">
                              <span className="fw-500 fs-14 text-dkblue me-2">
                                {txn.status ?? ""}
                              </span>
                              {txn.amount != null && Number(txn.amount) > 0 && (
                                <>
                                  <div
                                    style={{
                                      width: "4px",
                                      height: "4px",
                                      marginRight: "4px",
                                      borderRadius: "50%",
                                      backgroundColor: "#D9D9D9",
                                    }}
                                  ></div>
                                  <span className="fw-500 fs-14 textblue me-2">
                                    {currencySymbol} {txn.amount}
                                  </span>
                                </>
                              )}
                            </div>
                            <span className="fw-500 fs-14 text-grey me-2">
                              {txn.date} at {txn.time}
                            </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {active === "workflow" &&
                (() => {
                  let formattedDate = "";
                  if (workflowList.length > 0 && workflowList[0].date) {
                    const dateObj = new Date(workflowList[0].date);
                    formattedDate = dateObj.toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    });
                  }
                  return (
                    <div className="mt-3">
                      <div className="q-customer-card p-4" style={{ padding: "16px" }}>
                        <div className="fs-16 fw-600 text-dkblue mb-1">Workflow</div>
                        {isWorkflowLoading ? (
                          <div className="d-flex justify-content-center py-4">
                            <Loader height="150px" />
                          </div>
                        ) : (
                          <div
                            className="py-4 custom-scrollbar"
                            style={{
                              overflowX: "hidden",
                              maxHeight: "calc(100vh - 220px)",
                              overflowY: "auto",
                              paddingRight: "8px",
                              paddingBottom: "24px",
                            }}
                          >
                            <div>
                              <div style={{ minWidth: "300px" }}>
                                {formattedDate && (
                                  <div className="position-relative text-center mb-4">
                                    <hr />
                                    <span
                                      className="position-absolute top-50 start-50 translate-middle bg-white px-4 py-1 text-muted small border rounded-pill"
                                      style={{ fontWeight: 500, fontSize: "15px" }}
                                    >
                                      {formattedDate}
                                    </span>
                                  </div>
                                )}

                                {workflowList && workflowList.length > 0 ? (
                                  workflowList.map((workflowItem, index) => {
                                    const statusKey =
                                      workflowItem.status?.toLowerCase() || "";
                                    const Icon =
                                      workflowIcons[statusKey] ||
                                      (statusKey.startsWith("validity extended")
                                        ? <FaClock style={{ color: "#2E7D32" }} />
                                        : workflowIcons["pending"]);

                                    return (
                                      <div
                                        key={workflowItem.id ?? index}
                                        className="d-flex align-items-stretch"
                                        style={{ marginBottom: "24px" }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            flexDirection: "row",
                                            alignItems: "start",
                                            marginRight: "1rem",
                                            alignSelf: "stretch",
                                          }}
                                        >
                                          <div
                                            className="small text-muted mb-1"
                                            style={{
                                              marginRight: "1rem",
                                              minWidth: "60px",
                                            }}
                                          >
                                            {workflowItem.time}
                                          </div>

                                          <div
                                            style={{
                                              position: "relative",
                                              display: "flex",
                                              flexDirection: "column",
                                              alignItems: "center",
                                              alignSelf: "stretch",
                                            }}
                                          >
                                            <div
                                              style={{
                                                backgroundColor: "#f8f9fa",
                                                border: "1px solid #dee2e6",
                                                borderRadius: "50%",
                                                width: "30px",
                                                height: "30px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                zIndex: 1,
                                              }}
                                            >
                                              {Icon}
                                            </div>

                                            {index !== workflowList.length - 1 && (
                                              <div
                                                style={{
                                                  flex: 1,
                                                  width: "0px",
                                                  borderLeft: "1.5px dashed #D3D8E0",
                                                  marginTop: "4px",
                                                  marginBottom: "-24px",
                                                }}
                                              />
                                            )}
                                          </div>
                                        </div>

                                        <div style={{ flex: 1 }} className="d-flex align-items-start justify-content-between">
                                          <div>
                                            <p className="mb-1">
                                              <strong
                                                style={{
                                                  fontWeight: 500,
                                                  fontSize: "16px",
                                                  color: "#21272C",
                                                }}
                                              >
                                                {workflowItem.status}
                                              </strong>
                                            </p>
                                            <div className="small text-muted">
                                              By{" "}
                                              {workflowItem.created_by || "System"}{" "}
                                              on {workflowItem.date}
                                            </div>
                                          </div>
                                          {workflowItem.is_delete === true && hasDeletePermission && (
                                            <div
                                              onClick={() => {
                                                setHistoryDeleteId(workflowItem.id);
                                                setIsOpenHistoryDelete(true);
                                              }}
                                              title="Delete this workflow entry"
                                              style={{
                                                cursor: "pointer",
                                                width: "28px",
                                                height: "28px",
                                                borderRadius: "100px",
                                                border: "1px solid #E5E5E5",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                                marginLeft: "10px",
                                              }}
                                            >
                                              <FaTrash size={12} color="#FF4444" />
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div
                                    style={{
                                      textAlign: "center",
                                      padding: "40px",
                                      color: "#999",
                                      fontSize: "14px",
                                    }}
                                  >
                                    No workflow found
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
            </>
          )}
          {(isQuotView || templatePreviewUrl) && (
            <div
              style={{
                position: "relative",
                width: "100%",
                height: "100vh",
              }}
            >
              {/* Close Icon */}
              <div
                onClick={() => {
                  setIsQuotView(false);
                  setTemplatePreviewUrl(null);
                }}
                style={{
                  position: "absolute",
                  top: "12px",
                  right: "12px",
                  zIndex: 9999,
                  background: "#fff",
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                }}
              >
                ✕
              </div>

              {/* Share Icon — only enabled while a template preview is open */}
              {templatePreviewUrl && (
                <div
                  onClick={handleShareTemplatePreview}
                  title="Share on WhatsApp"
                  style={{
                    position: "absolute",
                    top: "12px",
                    right: "56px",
                    zIndex: 9999,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "0 16px",
                    height: "32px",
                    borderRadius: "100px",
                    backgroundColor: "#25D366",
                    color: "#fff",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  }}
                >
                  <FaWhatsapp size={14} />
                  Share
                </div>
              )}

              {/* PDF VIEW */}
              <iframe
                src={templatePreviewUrl ?? quotation?.pdf_view ?? ""}
                style={{ width: "100%", height: "100%", border: "none" }}
                title="PDF Viewer"
              />
            </div>
          )}
        </Col>
      </Row>

      {/* File Preview Modal (Image & PDF) */}
      {previewModal && previewData && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
          }}
          onClick={closePreviewModal}
        >
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
              maxWidth: "90%",
              maxHeight: "90%",
              overflow: "auto",
              position: "relative",
              width: previewData.type === "pdf" ? "95%" : "90%",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: "16px 24px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
              }}
            >
              <div style={{ fontSize: "18px", fontWeight: "600", color: "#1f2937" }}>
                {previewData.type === "pdf" ? "PDF Preview" : "Image Preview"}
              </div>
              <button
                onClick={closePreviewModal}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6b7280",
                  padding: "4px 8px",
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Body */}
            <div
              style={{
                padding: "24px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: previewData.type === "pdf" ? "600px" : "400px",
              }}
            >
              {previewData.type === "image" && (
                <div style={{ textAlign: "center" }}>
                  <img
                    src={previewData.url}
                    alt={previewData.name}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    }}
                  />
                </div>
              )}
              {previewData.type === "pdf" && (
                <iframe
                  src={previewData.url}
                  type="application/pdf"
                  width="100%"
                  height="550px"
                  style={{
                    border: "none",
                    borderRadius: "8px",
                  }}
                  title={previewData.name}
                />
              )}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: "12px 24px",
                borderTop: "1px solid #e5e7eb",
                background: "#f9fafb",
                textAlign: "center",
                borderRadius: "0 0 12px 12px",
              }}
            >
              <small style={{ color: "#6b7280", marginBottom: "12px", display: "block" }}>
                {previewData.name}
              </small>
              <button
                onClick={closePreviewModal}
                style={{
                  padding: "8px 20px",
                  backgroundColor: "#6b7280",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "8px",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#4b5563";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#6b7280";
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Attachment Delete Modal */}
      <AttachmentDeleteModal
        isOpen={attachmentDeleteModal}
        attachment={attachmentToDelete}
        isDeleting={isDeleting}
        onConfirm={confirmDeleteAttachment}
        onCancel={cancelDeleteAttachment}
      />

      <LeadConversionModal
        isOpen={isLeadConvertOpen}
        onClose={() => setIsLeadConvertOpen(false)}
        leadData={{
          id: quotation?.lead_id,
          lead_name: convertLead?.lead_name ?? quotation?.customer,
          company_name: convertLead?.company_name,
          first_name: convertLead?.first_name ?? quotation?.customer,
          phone: convertLead?.phone_number || convertLead?.contact_number1 || quotation?.phone,
          email: convertLead?.email ?? quotation?.contact_person_email,
        }}
        conversionInfo={leadConversionInfo}
        onConfirm={handleLeadConverted}
      />
    </div>
  );
};

export default QuotationView;
